import {
  Activity,
  BarChart3,
  BookOpen,
  CalendarDays,
  Check,
  Clock3,
  Code2,
  Compass,
  Copy,
  Database,
  Flame,
  FolderGit2,
  Mail,
  RefreshCw,
  Search,
  Sparkles,
  TrendingUp,
} from 'lucide-react'
import { useCallback, useEffect, useRef, useState } from 'react'
import { Link, useLocation, useNavigate } from 'react-router-dom'
import { createSubscription, getDaily } from '../api.js'
import { useAuth } from '../auth-context.js'
import BackButton from '../components/BackButton.jsx'
import EmptyState from '../components/EmptyState.jsx'
import ErrorState from '../components/ErrorState.jsx'
import LoadingState from '../components/LoadingState.jsx'
import Pagination from '../components/Pagination.jsx'
import RepoCard from '../components/RepoCard.jsx'
import { SearchInput, StatsCard } from '../components/ui/index.js'
import { setPageMetadata } from '../page-metadata.js'
import { dailyRankingNotice, shanghaiDate } from '../ranking-display.js'
import {
  buildDailyTopShareText,
  summarizeDailyRepos,
} from '../repo-insights.js'

async function copyText(value) {
  if (navigator.clipboard?.writeText) {
    try {
      await navigator.clipboard.writeText(value)
      return
    } catch {
      // Use the selection fallback when clipboard permission is unavailable.
    }
  }

  const textarea = document.createElement('textarea')
  textarea.value = value
  textarea.setAttribute('readonly', '')
  textarea.style.position = 'fixed'
  textarea.style.left = '-9999px'
  textarea.style.opacity = '0'
  document.body.appendChild(textarea)
  textarea.focus()
  textarea.select()
  textarea.setSelectionRange(0, textarea.value.length)
  const copied = document.execCommand('copy')
  textarea.remove()
  if (!copied) throw new Error('COPY_FAILED')
}

function formatCount(value, fallback = '待统计') {
  const number = Number(value)
  if (!Number.isFinite(number) || number <= 0) return fallback
  return new Intl.NumberFormat('zh-CN').format(number)
}

function HomePage() {
  const { user } = useAuth()
  const navigate = useNavigate()
  const location = useLocation()
  const isAdmin = user?.isAdmin === true || user?.is_admin === true
  const backLabel = location.state?.backLabel || '返回首页'
  const fallbackPath = location.state?.fallbackPath || '/'
  const [date, setDate] = useState(shanghaiDate)
  const [data, setData] = useState(null)
  const [summaryItems, setSummaryItems] = useState([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState('')
  const [page, setPage] = useState(1)
  const [sort, setSort] = useState('heat')
  const [homeSearch, setHomeSearch] = useState('')
  const [shareStatus, setShareStatus] = useState(null)
  const [subscriptionEmail, setSubscriptionEmail] = useState('')
  const [subscriptionStatus, setSubscriptionStatus] = useState(null)
  const [subscribing, setSubscribing] = useState(false)
  const shareTimerRef = useRef(null)
  const projectsSectionRef = useRef(null)

  const scrollToHomeSection = useCallback((sectionId) => {
    document.getElementById(sectionId)?.scrollIntoView({
      behavior: 'smooth',
      block: 'start',
    })
  }, [])

  useEffect(() => setPageMetadata(), [])

  useEffect(() => () => window.clearTimeout(shareTimerRef.current), [])

  useEffect(() => {
    const sectionByHash = {
      '#today-trends': 'today-trends',
      '#subscribe-section': 'subscribe-section',
    }
    const sectionId = sectionByHash[location.hash]

    if (!sectionId) return undefined

    const frameId = window.requestAnimationFrame(() => {
      scrollToHomeSection(sectionId)
    })

    return () => window.cancelAnimationFrame(frameId)
  }, [location.hash, scrollToHomeSection])

  const load = useCallback((signal) => {
    setLoading(true)
    setError('')
    getDaily(date, page, 10, sort, signal)
      .then(setData)
      .catch((requestError) => {
        if (requestError.name !== 'AbortError') setError(requestError.message)
      })
      .finally(() => setLoading(false))
  }, [date, page, sort])

  useEffect(() => {
    const controller = new AbortController()
    load(controller.signal)
    return () => controller.abort()
  }, [load])

  useEffect(() => {
    const controller = new AbortController()
    setSummaryItems([])
    getDaily(date, 1, 50, 'heat', controller.signal)
      .then((result) => setSummaryItems(result.items || []))
      .catch((requestError) => {
        if (requestError.name !== 'AbortError') setSummaryItems([])
      })
    return () => controller.abort()
  }, [date])

  const updateFavorite = (repoId, isFavorited) => {
    setData((current) => ({
      ...current,
      items: current.items.map((item) => (
        item.id === repoId ? { ...item, isFavorited } : item
      )),
    }))
  }

  const copyDailyTopFive = async () => {
    const items = summaryItems.length > 0 ? summaryItems : data?.items || []
    const homeUrl = new URL(window.location.origin)
    homeUrl.hash = '/'
    try {
      await copyText(buildDailyTopShareText(
        items,
        data?.actualDate || date,
        homeUrl.href,
      ))
      setShareStatus({ type: 'success', message: '今日 Top 5 已复制，可以直接粘贴发布。' })
    } catch {
      setShareStatus({ type: 'error', message: '复制失败，请稍后重试。' })
    }
    window.clearTimeout(shareTimerRef.current)
    shareTimerRef.current = window.setTimeout(() => setShareStatus(null), 2600)
  }

  const submitHomeSearch = (event) => {
    event.preventDefault()
    const term = homeSearch.trim()
    if (term) navigate(`/search?q=${encodeURIComponent(term)}`)
  }

  const submitSubscription = async (event) => {
    event.preventDefault()
    setSubscribing(true)
    setSubscriptionStatus(null)
    try {
      const result = await createSubscription(subscriptionEmail, 'weekly')
      setSubscriptionEmail('')
      setSubscriptionStatus({
        type: 'success',
        message: result.message || '订阅成功，请查收确认邮件。',
      })
    } catch (requestError) {
      setSubscriptionStatus({
        type: 'error',
        message: requestError.message || '订阅失败，请稍后重试。',
      })
    } finally {
      setSubscribing(false)
    }
  }

  const handlePageChange = (nextPage) => {
    setPage(nextPage)
    requestAnimationFrame(() => {
      projectsSectionRef.current?.scrollIntoView({
        behavior: 'smooth',
        block: 'start',
      })
    })
  }

  const summarySource = summaryItems.length > 0 ? summaryItems : data?.items || []
  const totalRepos = data?.pagination?.total ?? data?.items?.length ?? summarySource.length
  const trendSummary = summarizeDailyRepos(summarySource, totalRepos)
  const aiInsightCount = summarySource.filter((repo) => (
    repo.summary
    || repo.reason
    || repo.learningValue
    || repo.technicalHighlights
    || repo.aiStatus
  )).length
  const languageCount = trendSummary.languages.length
  const sortMeta = {
    heat: { label: '综合热度', icon: <Flame size={17} /> },
    growth: { label: 'Star 增长', icon: <TrendingUp size={17} /> },
    views: { label: '浏览最多', icon: <BarChart3 size={17} /> },
    latest: { label: '最新收录', icon: <Clock3 size={17} /> },
  }[sort]
  const hasItems = !loading && !error && data?.items?.length > 0
  const shareDisabled = loading || (summaryItems.length === 0 && !data?.items?.length)

  return (
    <div className="page-container home-page">
      <BackButton fallbackPath={fallbackPath}>{backLabel}</BackButton>

      <section className="home-hero" aria-labelledby="home-hero-title">
        <div className="home-hero-copy">
          <span className="eyebrow">GitHub Trends</span>
          <h1 id="home-hero-title">每天发现值得关注的 GitHub 开源项目</h1>
          <p>
            自动追踪 GitHub 热门仓库，结合 AI 生成中文解读，帮助开发者发现项目、学习技术、准备简历。
          </p>
          <div className="home-hero-actions">
            <button
              className="primary-button"
              type="button"
              onClick={() => scrollToHomeSection('today-trends')}
            >
              <Compass size={18} />
              查看今日趋势
            </button>
            <button
              className="secondary-button"
              type="button"
              onClick={() => scrollToHomeSection('subscribe-section')}
            >
              <Mail size={18} />
              订阅日报
            </button>
            <button
              className="secondary-button"
              type="button"
              onClick={() => navigate('/github-analysis')}
            >
              <Activity size={18} />
              GitHub项目分析
            </button>
          </div>
        </div>

        <div className="home-hero-stats" aria-label="首页核心数据">
          <StatsCard
            icon={<FolderGit2 size={20} />}
            label="今日项目数"
            value={trendSummary.hasData ? formatCount(trendSummary.total) : '待发布'}
            hint={data?.actualDate || date}
            tone="primary"
          />
          <StatsCard
            icon={<Code2 size={20} />}
            label="支持语言数"
            value={languageCount > 0 ? formatCount(languageCount) : '待统计'}
            hint={trendSummary.languages.join(' / ') || '等待榜单生成'}
          />
          <StatsCard
            icon={<Sparkles size={20} />}
            label="AI 解读数"
            value={aiInsightCount > 0 ? formatCount(aiInsightCount) : '待统计'}
            hint="基于已收录项目"
          />
          <StatsCard
            icon={<Database size={20} />}
            label="已收录项目数"
            value={totalRepos > 0 ? formatCount(totalRepos) : '待统计'}
            hint="来自当前榜单接口"
          />
        </div>
      </section>

      <section className="home-health-entry" aria-label="GitHub项目分析">
        <span className="home-health-entry-icon">
          <Activity size={20} />
        </span>
        <div>
          <h2>GitHub项目分析</h2>
          <p>输入仓库地址，自动生成项目亮点、技术栈、优化建议和面试表达</p>
        </div>
        <button
          className="secondary-button"
          type="button"
          onClick={() => navigate('/github-analysis')}
        >
          开始项目分析
        </button>
      </section>

      <section className="home-control-panel" aria-label="搜索和筛选">
        <form className="home-search-form" onSubmit={submitHomeSearch}>
          <SearchInput
            value={homeSearch}
            onChange={(event) => setHomeSearch(event.target.value)}
            placeholder="搜索项目、语言或关键词"
            aria-label="搜索项目、语言或关键词"
          />
          <button className="primary-button" type="submit">
            <Search size={17} />
            搜索项目
          </button>
        </form>

        <div className="home-filter-row">
          <label className="date-control">
            <CalendarDays size={18} />
            <input
              type="date"
              value={date}
              max={shanghaiDate()}
              onChange={(event) => {
                setDate(event.target.value)
                setPage(1)
              }}
              aria-label="选择榜单日期"
            />
          </label>

          <label className="home-select-control">
            排序
            <select
              aria-label="首页项目排序"
              value={sort}
              onChange={(event) => {
                setSort(event.target.value)
                setPage(1)
              }}
            >
              <option value="heat">综合热度</option>
              <option value="growth">Star 增长</option>
              <option value="views">浏览最多</option>
              <option value="latest">最新收录</option>
            </select>
          </label>

          <button
            className={`secondary-button home-share-button ${shareStatus?.type === 'success' ? 'is-copied' : ''}`}
            type="button"
            onClick={copyDailyTopFive}
            disabled={shareDisabled}
          >
            {shareStatus?.type === 'success' ? <Check size={17} /> : <Copy size={17} />}
            {shareStatus?.type === 'success' ? '已复制 Top 5' : '复制今日 Top 5'}
          </button>
        </div>

        {shareStatus && (
          <p
            className={`home-share-feedback ${shareStatus.type === 'error' ? 'is-error' : ''}`}
            role="status"
          >
            {shareStatus.message}
          </p>
        )}
      </section>

      {!loading && !error && (
        <section className="home-trend-overview" aria-labelledby="trend-summary-title">
          <div className="home-section-heading">
            <div>
              <span className="eyebrow">Today at a glance</span>
              <h2 id="trend-summary-title">今日趋势概览</h2>
            </div>
            <span>{data?.actualDate || date}</span>
          </div>
          <div className="home-stats-grid">
            <StatsCard
              icon={<FolderGit2 size={19} />}
              label="今日项目数量"
              value={trendSummary.hasData ? formatCount(trendSummary.total) : '待发布'}
              hint="按当前榜单数据统计"
              tone="primary"
            />
            <StatsCard
              icon={<Code2 size={19} />}
              label="主要语言 Top 3"
              value={trendSummary.languages.length > 0 ? trendSummary.languages.join(' / ') : '数据积累中'}
              hint="来自已加载项目"
            />
            <StatsCard
              icon={<Compass size={19} />}
              label="热门方向"
              value={trendSummary.keywords.length > 0 ? trendSummary.keywords.join(' / ') : '等待更多项目'}
              hint="基于标签和项目解读"
            />
            <StatsCard
              icon={<BookOpen size={19} />}
              label="适合学习"
              value={trendSummary.hasData ? `${trendSummary.learningCount} 个` : '待分析'}
              hint="可用于学习或作品集参考"
            />
          </div>
        </section>
      )}

      <section
        className="home-projects-section"
        id="today-trends"
        ref={projectsSectionRef}
        aria-labelledby="today-projects-title"
      >
        <div className="home-section-heading">
          <div>
            <span className="eyebrow">Recommended today</span>
            <h2 id="today-projects-title">今日推荐项目</h2>
          </div>
          <div className="home-section-meta">
            <span className="home-sort-chip">
              {sortMeta.icon}
              {sortMeta.label}
            </span>
            <span>{formatCount(totalRepos, '0')} 个项目</span>
          </div>
        </div>

        {!loading && !error && dailyRankingNotice(data) && data?.items?.length > 0 && (
          <p
            className={`ranking-notice ${data?.isFallback ? 'is-warning' : ''}`}
            role="status"
          >
            {dailyRankingNotice(data)}
          </p>
        )}

        {loading && <LoadingState label="正在读取今日榜单" />}
        {!loading && error && <ErrorState message={error} onRetry={() => load()} />}
        {!loading && !error && data?.items?.length === 0 && (
          <EmptyState
            icon={<RefreshCw size={28} />}
            title="今日榜单正在生成"
            message={data?.message || '今日榜单正在生成中，请稍后刷新页面。'}
            action={isAdmin ? (
              <Link className="secondary-button" to="/admin">
                <RefreshCw size={16} />
                手动刷新今日榜单
              </Link>
            ) : null}
          />
        )}
        {hasItems && (
          <>
            <div className="repo-list home-repo-grid">
              {data.items.map((repo) => (
                <RepoCard key={repo.id} repo={repo} onFavoriteChange={updateFavorite} />
              ))}
            </div>
            <Pagination
              pagination={data.pagination}
              onPageChange={handlePageChange}
              disabled={loading}
            />
          </>
        )}
      </section>

      <section className="subscription-card home-subscription-card" id="subscribe-section" aria-labelledby="subscription-title">
        <div className="subscription-copy">
          <span className="subscription-icon"><Mail size={20} /></span>
          <div>
            <span className="subscription-kicker">订阅 GitHub 开源精选</span>
            <h2 id="subscription-title">订阅 GitHub 趋势</h2>
            <p>每天/每周把值得关注的开源项目发送到你的邮箱。</p>
          </div>
        </div>
        <form className="subscription-form" onSubmit={submitSubscription}>
          <input
            type="email"
            value={subscriptionEmail}
            onChange={(event) => setSubscriptionEmail(event.target.value)}
            placeholder="你的邮箱"
            aria-label="订阅邮箱"
            maxLength={190}
            required
          />
          <select aria-label="订阅频率" value="weekly" disabled>
            <option value="weekly">每周精选</option>
          </select>
          <button className="primary-button" type="submit" disabled={subscribing}>
            {subscribing ? '订阅中...' : '订阅'}
          </button>
        </form>
        {subscriptionStatus && (
          <p
            className={`subscription-feedback ${subscriptionStatus.type === 'error' ? 'is-error' : ''}`}
            role="status"
          >
            {subscriptionStatus.type === 'success'
              ? '订阅成功，请查收确认邮件。'
              : subscriptionStatus.message}
          </p>
        )}
      </section>
    </div>
  )
}

export default HomePage
